import styled, { css } from 'styled-components';
import { themeGet, themeGetColor } from '../../foundation/theme/themeGet/get.js';
import { pxToRem } from '../../foundation/spacing/utils.js';
import { aiBackgroundColor, aiTextColor, aiBorderColor } from '../../foundation/theme/utils.js';
import { Typography } from '../../foundation/Typography/styled.js';

var containerSize = {
  '2xl': 56,
  xl: 48,
  lg: 40,
  md: 32,
  sm: 24,
  xs: 20,
  xxs: 16
};
var letterFontSize = {
  '2xl': 18,
  xl: 18,
  lg: 16,
  md: 14,
  sm: 12,
  xs: 10,
  xxs: 8
};
var iconSize = {
  '2xl': 24,
  xl: 20,
  lg: 16,
  md: 16,
  sm: 12,
  xs: 12,
  xxs: 8
};
var backgroundColor = {
  primary: themeGet('colors.secondary.50'),
  secondary: themeGet('colors.primary.50'),
  success: themeGet('colors.green.50'),
  danger: themeGet('colors.red.50'),
  warning: themeGet('colors.yellow.50'),
  info: themeGet('colors.blue.50'),
  assistant: themeGet('colors.assistant.50'),
  ai: '',
  // defined in /modules/v2/foundation/theme/utils.ts
  magenta: themeGet('colors.magenta.50'),
  cyan: themeGet('colors.cyan.50'),
  orange: themeGet('colors.orange.50'),
  brown: themeGet('colors.brown.50'),
  indigo: themeGet('colors.indigo.50'),
  light: themeGet('colors.background.50'),
  dark: themeGet('colors.gray.50'),
  ghost: themeGetColor('background.50', 0.32)
};
var backgroundColorSolid = {
  primary: themeGet('colors.secondary.500'),
  secondary: themeGet('colors.primary.500'),
  success: themeGet('colors.green.500'),
  danger: themeGet('colors.red.500'),
  warning: themeGet('colors.yellow.500'),
  info: themeGet('colors.blue.500'),
  assistant: themeGet('colors.assistant.500'),
  ai: '',
  // defined in /modules/v2/foundation/theme/utils.ts
  magenta: themeGet('colors.magenta.500'),
  cyan: themeGet('colors.cyan.500'),
  brown: themeGet('colors.brown.500'),
  indigo: themeGet('colors.indigo.500'),
  orange: themeGet('colors.orange.500'),
  light: themeGet('colors.background.50'),
  dark: themeGet('colors.gray.1000'),
  ghost: themeGetColor('background.50', 0.32)
};
var iconColor = {
  primary: themeGet('colors.secondary.500'),
  secondary: themeGet('colors.primary.600'),
  success: themeGet('colors.green.700'),
  danger: themeGet('colors.red.500'),
  warning: themeGet('colors.yellow.900'),
  info: themeGet('colors.blue.600'),
  assistant: themeGet('colors.assistant.500'),
  ai: '',
  // defined in /modules/v2/foundation/theme/utils.ts
  magenta: themeGet('colors.magenta.500'),
  cyan: themeGet('colors.cyan.500'),
  brown: themeGet('colors.brown.500'),
  indigo: themeGet('colors.indigo.500'),
  orange: themeGet('colors.orange.500'),
  light: themeGet('colors.text.200'),
  dark: themeGet('colors.gray.0'),
  ghost: themeGet('colors.text.light')
};
var borderColor = {
  primary: themeGet('colors.secondary.100'),
  secondary: themeGet('colors.primary.100'),
  success: themeGet('colors.green.100'),
  danger: themeGet('colors.red.100'),
  warning: themeGet('colors.yellow.100'),
  info: themeGet('colors.blue.100'),
  assistant: themeGet('colors.assistant.100'),
  ai: '',
  // defined in /modules/v2/foundation/theme/utils.ts
  magenta: themeGet('colors.magenta.100'),
  cyan: themeGet('colors.cyan.100'),
  brown: themeGet('colors.brown.100'),
  indigo: themeGet('colors.indigo.100'),
  orange: themeGet('colors.orange.100'),
  light: themeGet('colors.border.100'),
  dark: themeGet('colors.border.100'),
  ghost: themeGet('colors.text.light')
};
var borderRadius = {
  circle: themeGet('borderRadius.circle'),
  square: themeGet('borderRadius.md')
};
var solidBorderStyles = css(["outline:", ";outline-offset:-", ";"], function (_ref) {
  var $appearance = _ref.$appearance;
  var shadowColor = themeGetColor('background.50', 0.32);
  if ($appearance === 'light') {
    shadowColor = borderColor.light;
  }
  if ($appearance === 'ghost') {
    shadowColor = 'transparent';
  }
  return css(["", " solid ", ""], pxToRem(1), shadowColor);
}, pxToRem(1));
var borderStyles = css(["border:", ";"], function (props) {
  return css(["", " solid ", ""], pxToRem(1), borderColor[props.$appearance]);
});
var basicColorStyles = css(["color:", ";"], function (props) {
  if (props.$appearance === 'light' && props.$solid) return themeGet('colors.text.200');
  if (props.$appearance === 'dark' && !props.$solid) return themeGet('colors.gray.1000');
  return props.$solid ? themeGet('colors.gray.0') : iconColor[props.$appearance];
});
var getColorStyles = function getColorStyles(props) {
  if (props.$appearance === 'ai') {
    if (props.$solid) {
      return css(["color:", ";"], themeGet('colors.gray.0'));
    }
    return aiTextColor('500');
  }
  return basicColorStyles;
};
var aiBackgroundColorStyles = css(["", ";"], function (_ref2) {
  var $solid = _ref2.$solid;
  return $solid ? aiBackgroundColor('500') : aiBackgroundColor('50');
});
var basicBackgroundColorStyles = css(["background-color:", ";"], function (props) {
  return props.$solid ? backgroundColorSolid[props.$appearance] : backgroundColor[props.$appearance];
});
var getBackgroundColorStyles = function getBackgroundColorStyles(props) {
  return props.$appearance === 'ai' ? aiBackgroundColorStyles : basicBackgroundColorStyles;
};
var getBorderStyles = function getBorderStyles(props) {
  if (props.$appearance === 'ai') {
    return aiBorderColor('100');
  }
  return props.$solid ? solidBorderStyles : borderStyles;
};
var Container = styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-1jbhcmq-0"
})(["display:flex;justify-content:center;align-items:center;flex-shrink:0;overflow:hidden;width:", ";height:", ";", " ", ";border-radius:", ";[data-icon='falcon']{", ";width:", ";height:", ";}", "{font-size:", ";text-transform:uppercase;", "}"], function (props) {
  return pxToRem(containerSize[props.$size]);
}, function (props) {
  return pxToRem(containerSize[props.$size]);
}, getBackgroundColorStyles, getBorderStyles, function (props) {
  return borderRadius[props.$shape];
}, getColorStyles, function (props) {
  return pxToRem(iconSize[props.$size]);
}, function (props) {
  return pxToRem(iconSize[props.$size]);
}, Typography, function (props) {
  return pxToRem(letterFontSize[props.$size]);
}, getColorStyles);

export { Container };
